import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://gtbuyspreadsheet.run";
const logoUrl = "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/54d75be42d5913aa14ca64dd22e5d1e3.jpeg";

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "GTBuy Spreadsheet Review 2026 — Is It Worth It?",
  description: "An honest, unfiltered review of the GTBuy Spreadsheet sourcing methodology. We cover the real pros and cons, ideal user profiles, and whether this system delivers enough value.",
  datePublished: "2026-07-01",
  dateModified: "2026-07-01",
  author: {
    "@type": "Organization",
    name: "GTBuy Spreadsheet",
    url: siteUrl,
  },
  publisher: {
    "@type": "Organization",
    name: "GTBuy Spreadsheet",
    logo: {
      "@type": "ImageObject",
      url: logoUrl,
    },
  },
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": `${siteUrl}/gtbuy-spreadsheet-review`,
  },
};

export default function GTBuySpreadsheetReview() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <Navbar />
      <main>
        <section className="w-full pt-24 pb-12 md:pt-32 md:pb-16 px-6 md:px-10 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-5">
              <Link to="/" className="text-sm text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer flex items-center gap-1"><i className="ri-arrow-left-line"></i> Back to Home</Link>
              <span className="text-foreground-300">/</span>
              <span className="text-sm text-foreground-500">Review</span>
            </div>
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-4">Review &middot; 8 min read</span>
            <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 leading-tight mb-4">GTBuy Spreadsheet Review 2026 — Is It Worth It?</h1>
            <p className="text-base text-foreground-600 leading-relaxed max-w-2xl">An honest, unfiltered review of the GTBuy Spreadsheet sourcing methodology. We cover the real pros and cons, ideal user profiles, and whether this system delivers enough value to justify adopting it for your ecommerce business.</p>
            <p className="text-xs text-foreground-400 mt-4">Published: July 2026</p>
          </div>
        </section>

        <section className="w-full px-6 md:px-10 pb-10">
          <div className="max-w-4xl mx-auto">
            <div className="aspect-[16/7] w-full rounded-2xl overflow-hidden bg-background-200">
              <img src="https://readdy.ai/api/search-image?query=Product%20review%20concept%20with%20laptop%20displaying%20five%20golden%20star%20ratings%20and%20detailed%20spreadsheet%20analysis%20on%20screen%20coffee%20cup%20notepad%20with%20handwritten%20pros%20and%20cons%20list%20on%20warm%20wooden%20desk%20cozy%20home%20office%20with%20bookshelf%20soft%20warm%20lighting%20trustworthy%20authentic%20photography%20high%20detail&width=1600&height=700&seq=article-review-header&orientation=landscape"
                alt="GTBuy Spreadsheet Review 2026" title="GTBuy Spreadsheet Review — Is It Worth It?" className="w-full h-full object-cover" loading="eager" />
            </div>
          </div>
        </section>

        <article className="w-full px-6 md:px-10 pb-16">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-10 mb-4">What GTBuy Spreadsheet Actually Is</h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">Let us be clear: GTBuy Spreadsheet is <strong>not an app or a software product</strong>. It is a structured methodology and template system for organizing ecommerce supplier data in a spreadsheet format. This is both its greatest strength and its most common point of misunderstanding.</p>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">If you are looking for a one-click automated solution that connects to supplier APIs and runs entirely on autopilot, this is not that. But if you want a <strong>flexible, deeply customizable system</strong> that grows with your business and never locks you into a platform, GTBuy delivers exactly that.</p>

            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-12 mb-4">Pros and Cons</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-background-100 rounded-xl p-5">
                <h3 className="font-heading text-lg text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-check-line text-green-600 text-xl"></i> Pros
                </h3>
                <ul className="space-y-2 text-sm text-foreground-700 leading-relaxed">
                  <li>&bull; Complete data ownership and portability</li>
                  <li>&bull; No monthly subscription or recurring fees</li>
                  <li>&bull; Infinitely customizable to any business model</li>
                  <li>&bull; Scales seamlessly from 10 to 1000+ products</li>
                  <li>&bull; Works offline and across all spreadsheet apps</li>
                  <li>&bull; Enforces disciplined data collection habits</li>
                </ul>
              </div>
              <div className="bg-background-100 rounded-xl p-5">
                <h3 className="font-heading text-lg text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-close-line text-red-500 text-xl"></i> Cons
                </h3>
                <ul className="space-y-2 text-sm text-foreground-700 leading-relaxed">
                  <li>&bull; Requires manual data entry and updates</li>
                  <li>&bull; Moderate learning curve for advanced features</li>
                  <li>&bull; No built-in API integrations</li>
                  <li>&bull; Relies on user discipline to maintain</li>
                  <li>&bull; Not a real-time inventory sync tool</li>
                </ul>
              </div>
            </div>

            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-12 mb-4">Who Should Use GTBuy?</h2>
            <ul className="list-disc list-inside space-y-3 mb-8 text-base text-foreground-700 leading-relaxed">
              <li><strong>Dropshippers managing multiple suppliers</strong> — compare pricing across sources in one view.</li>
              <li><strong>Wholesale buyers negotiating bulk orders</strong> — track historical pricing to strengthen your position.</li>
              <li><strong>Private label sellers sourcing manufacturers</strong> — organize quotes, samples, and quality notes systematically.</li>
              <li><strong>Ecommerce agencies managing client catalogs</strong> — maintain separate sheets for each client with consistent methodology.</li>
            </ul>

            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-12 mb-4">Final Verdict</h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">For the right user, <strong>GTBuy Spreadsheet is a game-changer</strong>. It brings rigor and structure to a process that most sellers handle chaotically. The system pays for itself the first time it prevents you from overpaying on a bulk order or reveals that your preferred supplier is 15% more expensive than a competitor you never seriously evaluated.</p>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">Is it worth it? <strong>Yes, if you are serious about ecommerce.</strong> If you treat your store as a real business and plan to scale beyond a hobby project, the GTBuy methodology will fundamentally improve how you approach product sourcing. The structure becomes second nature within weeks, and the savings compound indefinitely.</p>

            <p className="text-sm text-foreground-500 italic mb-4">
              More to explore:{" "}
              <Link to="/what-is-gtbuy-spreadsheet" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">What is GTBuy?</Link>{" "}&middot;{" "}
              <Link to="/gtbuy-vs-kakobuy" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">GTBuy vs Kakobuy</Link>{" "}&middot;{" "}
              <Link to="/" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">Homepage</Link>
            </p>
          </div>
        </article>

        <section className="relative w-full py-20 px-6 md:px-10 bg-foreground-950 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-background-50 mb-4">Try the GTBuy System Yourself</h2>
            <p className="text-foreground-400 text-sm mb-6">Visit KA Spreadsheet, browse 14 product categories, and start applying the structured sourcing methodology today.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">GTBuy Sheet — Shop Now</a>
              <Link to="/" className="px-6 py-3 rounded-full bg-foreground-800 text-background-50 font-medium text-sm hover:bg-foreground-700 transition-colors cursor-pointer whitespace-nowrap">Back to Homepage</Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}